// src/components/QuickAppointmentModal.jsx
import { useState, useMemo, useEffect, useRef } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, isSameMonth, isToday, isBefore, startOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  X,
  CalendarDays,
  Clock,
  User,
  Mail,
  Phone,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  ClipboardList,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { generarBloquesLibres } from '../utils/agendaUtils';
import { collection, onSnapshot } from 'firebase/firestore';
import { getDb } from '../services/firebase';

export default function QuickAppointmentModal({ 
  isOpen, 
  onClose, 
  profile,
  professionalId,
  services = [],
  reservations = {},
  onCreateAppointment
}) {
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [step, setStep] = useState(1); // 1: servicio, 2: fecha, 3: hora, 4: cliente
  const [saving, setSaving] = useState(false);
  
  // Formulario de cliente
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: profile?.country || 'UY',
  });

  // Estados para autocompletar clientes
  const [clients, setClients] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isClientSelected, setIsClientSelected] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Cargar clientes del profesional
  useEffect(() => {
    if (!professionalId) return;

    const clientsRef = collection(getDb(), 'profiles', professionalId, 'clients');
    const unsubscribe = onSnapshot(
      clientsRef,
      (snapshot) => {
        const clientsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClients(clientsData);
      },
      (error) => {
        console.error('Error cargando clientes:', error);
      }
    );

    return () => unsubscribe();
  }, [professionalId]);

  // Resetear al abrir
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedDate(null);
      setSelectedTime(null);
      setSelectedService(null);
      
      // Inicializar form con custom fields
      const initialForm = {
        name: '',
        email: '',
        phone: '',
        countryCode: profile?.country || 'UY',
      };
      
      // Agregar campos para custom fields
      if (profile?.customFields && profile.customFields.length > 0) {
        profile.customFields.forEach((_, index) => {
          initialForm[`custom_${index}`] = '';
        });
      }
      
      setForm(initialForm);
      setSearchResults([]);
      setIsClientSelected(false);
      setShowDropdown(false);
    }
  }, [isOpen, profile]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Obtener disponibilidad del mes actual
  const monthAvailability = useMemo(() => {
    if (!profile?.availability) return {};
    
    const availability = {};
    const disabledDates = profile.disabledDates || [];
    const overrides = profile.scheduleOverrides || {};
    
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    
    calendarDays.forEach(day => {
      const iso = format(day, 'yyyy-MM-dd');
      const today = startOfDay(new Date());
      
      // No permitir fechas pasadas
      if (isBefore(day, today)) {
        availability[iso] = { enabled: false, isPast: true };
        return;
      }
      
      // Verificar si está deshabilitado globalmente
      if (disabledDates.includes(iso)) {
        availability[iso] = { enabled: false, isDisabled: true };
        return;
      }
      
      // Verificar override específico
      if (overrides[iso]) {
        availability[iso] = {
          enabled: overrides[iso].enabled,
          from: overrides[iso].from,
          to: overrides[iso].to,
          isOverride: true,
        };
        return;
      }
      
      // Configuración semanal recurrente
      const dayKey = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][day.getDay()];
      const dayConfig = profile.availability[dayKey];
      
      if (dayConfig?.enabled && dayConfig.from < dayConfig.to) {
        availability[iso] = {
          enabled: true,
          from: dayConfig.from,
          to: dayConfig.to,
        };
      } else {
        availability[iso] = { enabled: false };
      }
    });
    
    return availability;
  }, [profile, currentMonth]);

  // Manejar cambio en el campo de nombre (búsqueda)
  const handleSearchChange = (e) => {
    const value = e.target.value;
    
    setForm({ ...form, name: value, email: '', phone: '' });
    setIsClientSelected(false);

    // Limpiar el timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Si el valor es muy corto, limpiar resultados
    if (!value || value.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    // Establecer nuevo timer para la búsqueda
    debounceTimerRef.current = setTimeout(() => {
      const lowered = value.toLowerCase();
      const results = clients.filter(
        (c) =>
          (c.name || '').toLowerCase().includes(lowered) ||
          (c.email || '').toLowerCase().includes(lowered)
      ).slice(0, 8);
      
      setSearchResults(results);
      setShowDropdown(results.length > 0);
    }, 200);
  };

  // Seleccionar un cliente del dropdown
  const handleClientSelect = (client) => {
    setForm({
      ...form,
      name: client.name || '',
      email: (client.email || '').toLowerCase(),
      phone: client.phone || '',
      countryCode: client.countryCode || profile?.country || 'UY',
    });
    setSearchResults([]);
    setIsClientSelected(true);
    setShowDropdown(false);
  };

  // Calcular horarios disponibles para la fecha seleccionada
  const availableTimes = useMemo(() => {
    if (!selectedDate || !selectedService || !profile) return [];
    
    const iso = format(selectedDate, 'yyyy-MM-dd');
    const dayAvailability = monthAvailability[iso];
    
    if (!dayAvailability?.enabled) return [];
    
    const baseStep = Number(profile.slotDuration) || 30;
    const serviceDuration = Number(selectedService.duration) || 30;
    const reservasDelDia = reservations[iso] || {};
    
    const strategy = (profile.isPremiumActive && profile.schedulingStrategy === 'hybrid')
      ? 'hybrid'
      : 'flexible';
    
    const minNotice = Number(profile.minBookingNotice) || 0;
    const minBookingDate = new Date(Date.now() + minNotice * 60000);
    
    const bloques = generarBloquesLibres(
      dayAvailability.from,
      dayAvailability.to,
      serviceDuration,
      baseStep,
      reservasDelDia,
      selectedDate,
      strategy,
      minBookingDate
    );
    
    return bloques;
  }, [selectedDate, selectedService, profile, monthAvailability, reservations]);

  // Renderizar calendario
  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

    return (
      <div className="space-y-3">
        {/* Header del mes */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          </button>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: es })}
          </h3>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <ChevronRight className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          </button>
        </div>

        {/* Días de la semana */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map(day => (
            <div key={day} className="text-center text-xs font-semibold text-slate-500 dark:text-slate-400 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Días del calendario */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map(day => {
            const iso = format(day, 'yyyy-MM-dd');
            const dayAvailability = monthAvailability[iso];
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isDayToday = isToday(day);
            const isSelected = selectedDate && format(selectedDate, 'yyyy-MM-dd') === iso;
            const isAvailable = dayAvailability?.enabled;
            const isPast = dayAvailability?.isPast;
            
            // Contar reservas del día
            const reservasCount = Object.keys(reservations[iso] || {}).length;

            return (
              <button
                key={iso}
                onClick={() => {
                  if (isAvailable && !isPast) {
                    setSelectedDate(day);
                    setSelectedTime(null);
                    if (step < 3) setStep(3);
                  }
                }}
                disabled={!isAvailable || isPast}
                className={`
                  relative aspect-square rounded-lg p-1 text-sm font-medium transition-all
                  ${!isCurrentMonth ? 'opacity-40' : ''}
                  ${isDayToday ? 'ring-2 ring-blue-500' : ''}
                  ${isSelected ? 'bg-blue-600 text-white shadow-lg scale-105' : ''}
                  ${isAvailable && !isSelected && !isPast ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30 hover:scale-105 cursor-pointer' : ''}
                  ${!isAvailable || isPast ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-600 cursor-not-allowed' : ''}
                `}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <span>{format(day, 'd')}</span>
                  {reservasCount > 0 && (
                    <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Leyenda */}
        <div className="flex items-center justify-center gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"></div>
            <span className="text-xs text-slate-600 dark:text-slate-400">Disponible</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"></div>
            <span className="text-xs text-slate-600 dark:text-slate-400">No disponible</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-600"></div>
            <span className="text-xs text-slate-600 dark:text-slate-400">Seleccionado</span>
          </div>
        </div>
      </div>
    );
  };

  // Manejar envío del formulario
  const handleSubmit = async () => {
    // Validaciones
    if (!selectedService) {
      toast.error('Debes seleccionar un servicio');
      return;
    }
    if (!selectedDate) {
      toast.error('Debes seleccionar una fecha');
      return;
    }
    if (!selectedTime) {
      toast.error('Debes seleccionar un horario');
      return;
    }
    if (!form.name.trim()) {
      toast.error('Debes ingresar el nombre del cliente');
      return;
    }
    if (!form.email.trim()) {
      toast.error('Debes ingresar el email del cliente');
      return;
    }

    // Validar custom fields requeridos
    if (profile?.customFields && profile.customFields.length > 0) {
      for (let i = 0; i < profile.customFields.length; i++) {
        const field = profile.customFields[i];
        if (field.required) {
          const fieldKey = `custom_${i}`;
          if (!form[fieldKey] || !form[fieldKey].trim()) {
            toast.error(`El campo "${field.label}" es requerido`);
            return;
          }
        }
      }
    }

    setSaving(true);
    try {
      // Recolectar respuestas de custom fields
      const customFieldAnswers = {};
      if (profile?.customFields && profile.customFields.length > 0) {
        profile.customFields.forEach((field, index) => {
          const fieldKey = `custom_${index}`;
          if (form[fieldKey] && form[fieldKey].trim()) {
            customFieldAnswers[field.label] = form[fieldKey].trim();
          }
        });
      }

      await onCreateAppointment({
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        service: selectedService,
        clientData: form,
        customFieldAnswers,
      });
      
      toast.success('¡Cita agendada exitosamente!');
      onClose();
    } catch (error) {
      console.error('Error creando cita:', error);
      toast.error('Error al crear la cita');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <CalendarDays className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Agendar Nueva Cita</h2>
              <p className="text-sm text-blue-100">Proceso rápido e intuitivo</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Indicadores de paso */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {[
              { num: 1, label: 'Servicio', icon: ClipboardList },
              { num: 2, label: 'Fecha', icon: CalendarDays },
              { num: 3, label: 'Hora', icon: Clock },
              { num: 4, label: 'Cliente', icon: User },
            ].map((item, index) => (
              <div key={item.num} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all
                    ${step >= item.num ? 'bg-blue-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-400'}
                    ${step === item.num ? 'ring-4 ring-blue-200 dark:ring-blue-900' : ''}
                  `}>
                    {step > item.num ? (
                      <CheckCircle className="h-6 w-6" />
                    ) : (
                      <item.icon className="h-5 w-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-1 font-medium ${step >= item.num ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'}`}>
                    {item.label}
                  </span>
                </div>
                {index < 3 && (
                  <div className={`w-12 sm:w-20 h-0.5 mb-6 mx-2 ${step > item.num ? 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contenido */}
        <div className="p-6">
          {/* Paso 1: Seleccionar Servicio */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">
                Selecciona el servicio
              </h3>
              {services.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-600 dark:text-slate-400">
                    No tienes servicios configurados. Ve a la sección Servicios para crear uno.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {services.map(service => (
                    <button
                      key={service.id}
                      onClick={() => {
                        setSelectedService(service);
                        setStep(2);
                      }}
                      className={`
                        p-4 rounded-xl border-2 text-left transition-all hover:scale-105
                        ${selectedService?.id === service.id
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700'
                        }
                      `}
                    >
                      <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-1">
                        {service.name}
                      </h4>
                      <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {service.duration} min
                        </span>
                        {service.price > 0 && (
                          <span className="font-semibold text-blue-600 dark:text-blue-400">
                            ${service.price}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Paso 2: Seleccionar Fecha */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Selecciona la fecha
                </h3>
                {selectedService && (
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    Servicio: <span className="font-semibold">{selectedService.name}</span>
                  </div>
                )}
              </div>
              {renderCalendar()}
            </div>
          )}

          {/* Paso 3: Seleccionar Hora */}
          {step === 3 && selectedDate && (
            <div className="space-y-4">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                  Selecciona el horario
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Fecha: <span className="font-semibold">{format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}</span>
                </p>
              </div>
              
              {availableTimes.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                  <Clock className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600 dark:text-slate-400">
                    No hay horarios disponibles para esta fecha
                  </p>
                  <button
                    onClick={() => {
                      setSelectedDate(null);
                      setStep(2);
                    }}
                    className="mt-4 text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
                  >
                    Elegir otra fecha
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-64 overflow-y-auto">
                  {availableTimes.map((bloque, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedTime(bloque.range);
                        setStep(4);
                      }}
                      className={`
                        p-3 rounded-lg font-medium transition-all hover:scale-105
                        ${selectedTime === bloque.range
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                        }
                      `}
                    >
                      <div className="text-sm">{bloque.range.split(' - ')[0]}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Paso 4: Datos del Cliente */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">
                Datos del cliente
              </h3>
              
              {/* Resumen de la cita */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Resumen de la cita</h4>
                <div className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
                  <p><strong>Servicio:</strong> {selectedService?.name}</p>
                  <p><strong>Fecha:</strong> {format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}</p>
                  <p><strong>Hora:</strong> {selectedTime}</p>
                  <p><strong>Duración:</strong> {selectedService?.duration} minutos</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Campo de búsqueda de cliente con autocompletar */}
                <div className="relative" ref={dropdownRef}>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    <User className="h-4 w-4 inline mr-1" />
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={handleSearchChange}
                    onFocus={() => {
                      if (searchResults.length > 0) setShowDropdown(true);
                    }}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                    placeholder="Buscar cliente o escribir nombre..."
                    autoComplete="off"
                  />
                  
                  {/* Dropdown de resultados */}
                  {showDropdown && searchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                      <ul className="py-1">
                        {searchResults.map((client) => (
                          <li
                            key={client.id}
                            onClick={() => handleClientSelect(client)}
                            className="px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                                {(client.name || '?').charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-slate-800 dark:text-slate-100 truncate">
                                  {client.name}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {client.email}
                                </p>
                                {client.phone && (
                                  <p className="text-xs text-slate-400 dark:text-slate-500">
                                    {client.phone}
                                  </p>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Indicador de cliente seleccionado */}
                  {isClientSelected && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                      <CheckCircle className="h-4 w-4" />
                      <span>Cliente existente seleccionado</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    <Mail className="h-4 w-4 inline mr-1" />
                    Email *
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    disabled={isClientSelected}
                    className={`w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 ${
                      isClientSelected ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                    placeholder="juan@ejemplo.com"
                  />
                  {isClientSelected && (
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Campo bloqueado porque seleccionaste un cliente existente
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    <Phone className="h-4 w-4 inline mr-1" />
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    disabled={isClientSelected}
                    className={`w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 ${
                      isClientSelected ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                    placeholder="+598 99 123 456"
                  />
                </div>

                {/* Botón para limpiar selección */}
                {isClientSelected && (
                  <button
                    type="button"
                    onClick={() => {
                      const clearedForm = {
                        name: '',
                        email: '',
                        phone: '',
                        countryCode: profile?.country || 'UY',
                      };
                      
                      // Resetear custom fields también
                      if (profile?.customFields && profile.customFields.length > 0) {
                        profile.customFields.forEach((_, index) => {
                          clearedForm[`custom_${index}`] = '';
                        });
                      }
                      
                      setForm(clearedForm);
                      setIsClientSelected(false);
                      setSearchResults([]);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    Limpiar y crear nuevo cliente
                  </button>
                )}

                {/* Campos personalizados */}
                {profile?.customFields && profile.customFields.length > 0 && (
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-700 space-y-4">
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Información adicional
                    </h4>
                    {profile.customFields.map((field, index) => {
                      const fieldKey = `custom_${index}`;
                      return (
                        <div key={fieldKey}>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            {field.label}
                            {field.required && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                            {!field.required && (
                              <span className="text-slate-400 dark:text-slate-500 ml-1 text-xs">(Opcional)</span>
                            )}
                          </label>
                          <input
                            type="text"
                            value={form[fieldKey] || ''}
                            onChange={(e) => setForm({ ...form, [fieldKey]: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                            placeholder={field.required ? `${field.label} (requerido)` : field.label}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer con botones */}
        <div className="sticky bottom-0 bg-slate-50 dark:bg-slate-900 p-6 rounded-b-2xl border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            {step > 1 && (
              <button
                onClick={() => {
                  if (step === 3 && selectedDate) setSelectedTime(null);
                  setStep(step - 1);
                }}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 font-medium"
              >
                ← Atrás
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 font-medium"
            >
              Cancelar
            </button>
            
            {step === 4 && (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {saving ? 'Guardando...' : 'Agendar Cita'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

